// React
import { useCallback, useState } from 'react';

// UI primitives
import { Counter } from '@ui/counter';
import { Button } from '@ui/button';

// Shared components
import { DataTableShortcuts } from '@components/DataTable/DataTableShortcuts';

// Icons
import { PlusIcon } from '@components/icons/PlusIcon';

// Local components
import { ResaleItemsTabs } from '../Sections/ResaleItemsTabs';
import { ResaleItemsTable } from '../Sections/ResaleItemsTable';
import { ResaleItemPreviewDrawer } from '../Sections/ResaleItemPreviewDrawer';
import { PageTitle } from '../../layouts/Page';

// Hooks
import { useTranslation } from '@hooks/useTranslation';

// Types
import type { AllResaleItemsPageProps } from '../types';

const DEFAULT_RESALE_ITEMS: never[] = [];

export function AllResaleItemsPage({
  currentStatus,
  totalCount = 0,
  onStatusChange,
  onCreateNewClick,
  onStatusToggle,
  resaleItems = DEFAULT_RESALE_ITEMS,
  isLoading = false,
  isFetching = false,
  isError = false,
  onRetry,
  selectedResaleItemId: controlledSelectedId,
  onSelectedResaleItemChange,
  canWrite = true,
}: AllResaleItemsPageProps) {
  const { t } = useTranslation();
  const countText = isLoading ? '…' : totalCount.toLocaleString();

  const [internalSelectedId, setInternalSelectedId] = useState<number | null>(null);
  const isControlled = controlledSelectedId !== undefined;
  const selectedResaleItemId = isControlled ? controlledSelectedId : internalSelectedId;
  const setSelectedResaleItemId = isControlled
    ? (id: number | null) => onSelectedResaleItemChange?.(id)
    : setInternalSelectedId;

  const handleItemClick = useCallback(
    (id: number) => setSelectedResaleItemId(id),
    [setSelectedResaleItemId], // eslint-disable-line react-hooks/exhaustive-deps -- setSelectedResaleItemId identity changes in controlled mode
  );

  const handleDrawerOpenChange = useCallback(
    (open: boolean) => {
      if (!open) setSelectedResaleItemId(null);
    },
    [setSelectedResaleItemId], // eslint-disable-line react-hooks/exhaustive-deps -- see above
  );

  const selectedResaleItem =
    selectedResaleItemId != null
      ? resaleItems.find((item) => item.id === selectedResaleItemId) ?? null
      : null;

  return (
    <>
      <DataTableShortcuts>
        <div className="flex flex-col gap-4 flex-1 min-h-0 px-6 sm:px-8 lg:px-16">
          <PageTitle>
            <h1 className="font-kapra-expanded text-2xl font-black uppercase leading-[1.5] text-off-black">
              {t('header.title')}
            </h1>
            <Counter value={countText} data-loading={isLoading || isFetching} />
          </PageTitle>

          <div className="flex flex-shrink-0 items-center justify-between gap-4">
            <ResaleItemsTabs
              currentStatus={currentStatus}
              onStatusChange={onStatusChange}
            />
            {canWrite && (
              <Button onClick={onCreateNewClick}>
                <PlusIcon className="size-5" aria-hidden="true" />
                {t('actions.new_resale_item')}
              </Button>
            )}
          </div>

          <ResaleItemsTable
            resaleItems={resaleItems}
            currentStatus={currentStatus}
            onStatusToggle={onStatusToggle}
            onItemClick={handleItemClick}
            isLoading={isLoading}
            isFetching={isFetching}
            isError={isError}
            onRetry={onRetry}
            canWrite={canWrite}
            className="min-h-0 flex-1"
          />
        </div>
      </DataTableShortcuts>

      <ResaleItemPreviewDrawer
        resaleItem={selectedResaleItem}
        open={selectedResaleItemId !== null}
        onOpenChange={handleDrawerOpenChange}
        canWrite={canWrite}
      />
    </>
  );
}
